import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, assetUrl, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import WheelCanvas from "@/components/public/WheelCanvas";
import RewardWheel from "@/components/public/RewardWheel";
import ScratchCard from "@/components/public/ScratchCard";
import { Slider } from "@/components/ui/slider";
import { Plus, Trash2, Upload, ArrowLeft, ArrowRight, Sparkles, Loader2, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import ThemeWrap, { THEME_ORDER, getThemeMeta } from "@/components/public/ThemeWrap";
import { composeTheme, slugifyBusinessType } from "@/lib/categoryThemes";
import { CitySelect } from "@/components/CitySelect";
import { parsePoolCodes } from "@/lib/poolParser";
import { normalizePalette } from "@/lib/safeColors";
import { purchaseListingFreeTier, payListingWithWallet } from "@/lib/listingDuration";

const THEMES = THEME_ORDER;
const SLICE_PALETTE = [
  "#E11D48", "#22C55E", "#F5D061", "#22D3EE", "#A78BFA", "#FB7185",
  "#34D399", "#F97316", "#60A5FA", "#F472B6", "#FACC15", "#0EA5E9",
];

/**
 * Client-side mirror of `_default_terms()` in routes/campaigns.py.
 *
 * Kept as a preview so the vendor can SEE the auto-filled boilerplate
 * BEFORE saving — the actual value written to the DB is still whatever
 * the server generates (or the vendor's edited text). Keep the wording
 * in exact sync with the backend so preview matches published output.
 *
 * Game-type-aware — same legal clauses, only the verb / phrase-of-play
 * changes. If a fourth game type ships, add a branch here AND in the
 * backend `_default_terms()` together.
 */
const buildDefaultTerms = (couponValidityDays, gameType = "spin_wheel") => {
  const days = Number.parseInt(couponValidityDays, 10) || 0;
  const dayWord = days === 1 ? "day" : "days";

  // Terminology map — mirrors the backend switch at
  // routes/campaigns.py::_default_terms.
  let verbGerund; let verbNoun; let playNoun; let line1;
  if (gameType === "static_offer") {
    verbGerund = "claiming"; verbNoun = "claim"; playNoun = "claim";
    line1 =
      `1. One ${verbNoun} per person, per phone number. ` +
      "Duplicate claims will be disqualified.";
  } else if (gameType === "scratch_card") {
    verbGerund = "scratching"; verbNoun = "scratch"; playNoun = "scratch";
    line1 =
      `1. One ${verbNoun} per person, per phone number. Attempts to game ` +
      "the card with multiple entries will be disqualified.";
  } else {
    verbGerund = "spinning"; verbNoun = "spin"; playNoun = "spin";
    line1 =
      `1. One ${verbNoun} per person, per phone number. Attempts to game ` +
      "the wheel with multiple entries will be disqualified.";
  }

  const validityLine =
    days > 0
      ? `Coupons must be redeemed within ${days} ${dayWord} of the ${playNoun} date, after which they expire and cannot be reissued.`
      : "Coupon validity is set by the store — see the coupon email for the expiry.";

  return (
    `${line1}\n` +
    "2. Prizes are subject to availability. The store reserves the right " +
    "to substitute a prize of equal or greater value if stock runs out.\n" +
    `3. ${validityLine}\n` +
    "4. Coupons are non-transferable and cannot be exchanged for cash.\n" +
    `5. By ${verbGerund}, you agree to receive your coupon and future offers ` +
    "over WhatsApp, SMS or email."
  );
};

const defaultSlices = () => {
  // PERCENTAGES are 0-100 and must sum to exactly 100 across all slices.
  // Absolute prize quantity = round(percentage * max_participants / 100).
  const seed = [
    { label: "50% Off", pct: 5 },
    { label: "20% Off", pct: 15 },
    { label: "10% Off", pct: 25 },
    { label: "Free Gift", pct: 15 },
    { label: "5% Off", pct: 25 },
    { label: "Better Luck", pct: 15, better: true },
  ];
  return seed.map((s, i) => ({
    label: s.label,
    gift_name: s.label,
    percentage: s.pct,
    color_hex: SLICE_PALETTE[i % SLICE_PALETTE.length],
    is_better_luck: !!s.better,
    image_url: "",
    terms: "",
    redirect_url: "",
  }));
};

export default function CampaignWizardPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const editing = Boolean(id);
  // A3 — when the wizard is mounted from
  // `/admin/vendors/:vendorId/campaigns/new?adminForVendor=<id>`, the
  // create submit goes through the admin-shim endpoint instead of
  // /campaigns. Everything else — including billing/trial gating —
  // is delegated to the vendor handler on the server, so no logic
  // forks per surface.
  const adminForVendor = searchParams.get("adminForVendor");

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [launched, setLaunched] = useState(null); // populated post-launch with {qr_code_url, campaign_url}

  const [form, setForm] = useState({
    name: "Festive Spin",
    game_type: "spin_wheel",
    theme: "citydooh",
    expires_at: "",
    max_participants: 100,
    spin_limit: "once",
    slices: defaultSlices(),
    logo_url: "",
    thumbnail_url: "",
    terms_and_conditions: "",
    primary_color: "#E11D48",
    secondary_color: "#22C55E",
    coupon_prefix: "CTD",
    coupon_validity_days: 30,
    custom_form_fields: [],
    require_email: true,
    custom_theme_bg: "",
    use_theme_style: true,
    require_email_otp: false,
    // Client-synth'd win sound. See /app/frontend/src/lib/spinSound.js.
    spin_sound: "classic",
    // Scratch Card's counterpart to spin_sound. Only shown in the
    // wizard when game_type = "scratch_card"; persisted always so a
    // vendor can flip between game types without losing their picks.
    scratch_sound: "classic",
    // Phase 2 — category theme + intensity + optional manual swatch variant
    category_theme: "",           // auto-detected from vendor.business_type when empty
    theme_intensity: "branded",   // "minimal" | "branded" | "immersive"
    manual_theme_variant: "",     // optional swatch override
    // Optional festive accent overlay — client-side pure CSS/SVG only.
    // "" (none) | "christmas" | "diwali" | "newyear" | "valentine" | "citydooh"
    seasonal_accent: "",
    // ── Explore listing toggle ────────────────────────────────────
    // Wired to the campaign document's `listed_publicly` field which
    // /api/public/campaigns filters on. Defaults to OFF (privacy-
    // first); vendors flip it on when they want the campaign to
    // appear in the /explore feed. /play/{id} stays reachable via
    // direct link regardless.
    listed_publicly: false,
    // ── Campaign-level city (Cities Phase-1) ──────────────────────
    // Where THIS specific campaign runs. Defaults to the vendor's
    // profile city (populated below in the vendor fetch effect) so
    // the picker isn't blank on first render. Editable per-campaign
    // — a Visakhapatnam HQ can run a Guntur-only campaign without
    // changing the vendor profile.
    campaign_city: "",
    // ── 02/08 Static Offer listing duration ───────────────────────
    // Only meaningful when game_type === "static_offer". The Wizard's
    // "Publish" step exposes a 7d Free / 15d ₹99 / 30d ₹149 picker;
    // the free tier activates listed_publicly immediately, the paid
    // tiers route through /listing/purchase → Razorpay Checkout →
    // /listing/convert-to-paid. Ignored for spin/scratch (whose
    // listed_publicly is free + indefinite by design).
    listing_duration_days: 7,
  });

  // Billing state — pulled once when the wizard loads so we can render
  // an appropriate banner (trial available / override active / ₹499
  // will be charged / whatsapp coming soon).
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    if (editing) return;
    api
      .meBilling()
      .then((r) => setBilling(r.data))
      .catch(() => setBilling(null));
  }, [editing]);

  // Vendor profile — pulled once so the live preview can auto-detect
  // the same Category Theme /play would resolve when the wizard's
  // `category_theme` field is left blank. Falls back gracefully when
  // the request fails; the preview will just use "other".
  const [vendorBusinessType, setVendorBusinessType] = useState("");
  useEffect(() => {
    api
      .me()
      .then((r) => {
        setVendorBusinessType(r?.data?.business_type || "");
        // Seed campaign_city + campaign logo from vendor profile on
        // new campaigns only — edits keep the campaign's own values.
        if (!editing) {
          const vcity = r?.data?.city || "";
          const vlogo = r?.data?.default_logo_url || "";
          setForm((f) => ({
            ...f,
            campaign_city: f.campaign_city || vcity,
            // A6 — auto-prefill logo when the wizard hasn't received
            // one from step-2 uploads yet. `logo_url` staying editable
            // means the vendor can still upload a per-campaign
            // override without any extra clicks.
            logo_url: f.logo_url || vlogo,
          }));
        }
      })
      .catch(() => setVendorBusinessType(""));
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    api.getCampaign(id).then((r) => {
      const c = r.data;
      const maxP = Number(c.max_participants || 100);
      setForm((f) => ({
        ...f,
        name: c.name,
        game_type: c.game_type,
        theme: c.theme,
        expires_at: c.expires_at ? c.expires_at.split("T")[0] : "",
        max_participants: maxP,
        spin_limit: c.spin_limit,
        slices: c.slices?.length
          ? c.slices.map((sl) => {
              // v3: prefer `percentage`. Fall back to deriving from quantity
              // (legacy v2 sum=100) or probability (v1).
              let pct = sl.percentage != null ? Number(sl.percentage) : null;
              if (pct == null || Number.isNaN(pct) || pct <= 0) {
                const q = Number(sl.quantity || 0);
                pct = maxP > 0 ? (q * 100) / maxP : Number(sl.probability || 0);
              }
              return {
                ...sl,
                percentage: Number(pct.toFixed(2)),
              };
            })
          : f.slices,
        logo_url: c.logo_url || "",
        thumbnail_url: c.thumbnail_url || "",
        terms_and_conditions: c.terms_and_conditions || "",
        primary_color: c.primary_color || "#E11D48",
        secondary_color: c.secondary_color || "#22C55E",
        coupon_prefix: c.coupon_prefix || "CTD",
        coupon_validity_days: c.coupon_validity_days || 30,
        custom_form_fields: c.custom_form_fields || [],
        require_email: c.require_email,
        custom_theme_bg: c.custom_theme_bg || "",
        use_theme_style: c.use_theme_style !== false,
        require_email_otp: !!c.require_email_otp,
        spin_sound: c.spin_sound || "classic",
        scratch_sound: c.scratch_sound || "classic",
        // Phase 2 — carry the persisted theme selection back into the form
        category_theme: c.category_theme || "",
        theme_intensity: c.theme_intensity || "branded",
        manual_theme_variant: c.manual_theme_variant || "",
        seasonal_accent: c.seasonal_accent || "",
        // Preserve Explore visibility on edit — the toggle is a
        // simple boolean, no defaulting-back-to-True surprises.
        listed_publicly: !!c.listed_publicly,
        // Preserve campaign_city on edit — falls back to empty when
        // the field is absent on legacy rows (server will keep them
        // findable via vendor.city fallback).
        campaign_city: c.campaign_city || "",
      }));
    });
  }, [editing, id]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const updateSlice = (idx, k, v) =>
    setForm((s) => ({
      ...s,
      slices: s.slices.map((sl, i) => (i === idx ? { ...sl, [k]: v } : sl)),
    }));
  const addSlice = () =>
    setForm((s) => {
      if (s.slices.length >= 12) return s;
      return {
        ...s,
        slices: [
          ...s.slices,
          {
            label: "New Prize",
            gift_name: "New Prize",
            percentage: 0,
            color_hex: SLICE_PALETTE[s.slices.length % SLICE_PALETTE.length],
            is_better_luck: false,
            image_url: "",
            terms: "",
            redirect_url: "",
          },
        ],
      };
    });
  const removeSlice = (i) =>
    setForm((s) => ({ ...s, slices: s.slices.filter((_, idx) => idx !== i) }));

  const setSliceCount = (count) => {
    setForm((s) => {
      const cur = s.slices;
      if (count === cur.length) return s;
      if (count > cur.length) {
        const add = Array.from({ length: count - cur.length }).map((_, i) => ({
          label: `Prize ${cur.length + i + 1}`,
          gift_name: `Prize ${cur.length + i + 1}`,
          percentage: 0,
          color_hex: SLICE_PALETTE[(cur.length + i) % SLICE_PALETTE.length],
          is_better_luck: false,
          image_url: "",
          terms: "",
          redirect_url: "",
        }));
        return { ...s, slices: [...cur, ...add] };
      }
      return { ...s, slices: cur.slice(0, count) };
    });
  };

  const onUpload = async (file, target) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await api.upload(file);
      if (target.kind === "logo") update("logo_url", r.data.url);
      else if (target.kind === "thumbnail") update("thumbnail_url", r.data.url);
      else if (target.kind === "bg") update("custom_theme_bg", r.data.url);
      else if (target.kind === "slice") updateSlice(target.idx, "image_url", r.data.url);
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const maxParticipants = Math.max(1, parseInt(form.max_participants || 1, 10));

  const totalPct = useMemo(
    () => form.slices.reduce((a, s) => a + Math.max(0, parseFloat(s.percentage || 0)), 0),
    [form.slices]
  );
  // Allow tiny float drift; rounded to 2 decimals must equal 100.
  const percentIsExact = Math.round(totalPct * 100) === 10000;
  const remaining = Math.round((100 - totalPct) * 100) / 100;
  const quantityStatus = totalPct > 100
    ? { color: "#DC2626", msg: `Over by ${Math.abs(remaining)}%`, valid: false }
    : totalPct < 100
    ? { color: "#D97706", msg: `${remaining}% remaining`, valid: false }
    : { color: "#16A34A", msg: "Perfect \u2014 ready to launch", valid: true };

  const sliceQty = (pct) => Math.round((Math.max(0, parseFloat(pct || 0)) * maxParticipants) / 100);
  const totalQty = useMemo(
    () => form.slices.reduce((a, s) => a + sliceQty(s.percentage), 0),
    [form.slices, maxParticipants]
  );

  const buildPayload = () => ({
    ...form,
    expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
    max_participants: maxParticipants,
    // Static offers persist exactly ONE slice at 100% — force the shape
    // here so the vendor doesn't need to reason about the percentage UI
    // (which is hidden for this game type anyway).
    slices: (form.game_type === "static_offer"
      ? form.slices.slice(0, 1)
      : form.slices
    ).map((s) => {
      const rawPct = form.game_type === "static_offer" ? 100 : Math.max(0, parseFloat(s.percentage || 0));
      const isBetterLuck = form.game_type === "static_offer" ? false : !!s.is_better_luck;
      const mode = isBetterLuck ? "auto" : ((s.coupon_mode || "auto").toLowerCase());
      // Per-slice redirect URL — empty string means "no override, use
      // platform default". We only send a value when the vendor
      // actually typed one, so unchanged legacy campaigns stay clean.
      const redirectUrlRaw = typeof s.redirect_url === "string" ? s.redirect_url.trim() : "";
      return {
        label: s.label,
        gift_name: s.gift_name || s.label,
        percentage: rawPct,
        // Mirror legacy fields so any older consumer keeps working.
        quantity: form.game_type === "static_offer" ? maxParticipants : sliceQty(rawPct),
        probability: rawPct,
        color_hex: s.color_hex,
        is_better_luck: isBetterLuck,
        image_url: s.image_url || null,
        terms: s.terms || null,
        redirect_url: redirectUrlRaw || null,
        id: s.id,
        // Coupon sourcing — only persist non-default fields when the
        // slice actually needs them, so the payload stays compact for
        // vanilla auto-mode slices.
        coupon_mode: mode,
        single_code: mode === "single" ? (s.single_code || "").trim() : null,
        // pool_codes_total / pool_codes_remaining are managed server-
        // side by the upload/spin endpoints — we preserve whatever the
        // vendor's most-recent GET returned so a wizard save doesn't
        // accidentally zero out the counters.
        pool_codes_total: mode === "pool" ? Number(s.pool_codes_total || 0) : 0,
        pool_codes_remaining: mode === "pool" ? Number(s.pool_codes_remaining || 0) : 0,
      };
    }),
    coupon_validity_days: Number(form.coupon_validity_days) || 30,
    listed_publicly: !!form.listed_publicly,
    // Only send campaign_city when the vendor picked something —
    // sending "" would blank a legacy row's inherited value. On
    // create the backend falls back to vendor.city if this is unset.
    ...(form.campaign_city ? { campaign_city: form.campaign_city } : {}),
    // ── Pool codes bundled with the create (v3.6 wizard) ──────────
    // Serialise the client-parsed codes keyed by slice INDEX
    // (0-based string), which the backend resolves against the
    // freshly-minted slice IDs. `canSubmit` already blocked any
    // mismatched files, but the server re-runs the exact-count check
    // as the mandatory safety net. Only sent on CREATE — edits use
    // the append-only upload endpoint per the spec.
    ...(() => {
      if (editing) return {};
      const mapping = {};
      (form.slices || []).forEach((s, i) => {
        const m = (s.coupon_mode || "auto").toLowerCase();
        const codes = Array.isArray(s._pending_pool_codes) ? s._pending_pool_codes : [];
        if (m === "pool" && codes.length > 0) mapping[String(i)] = codes;
      });
      return Object.keys(mapping).length ? { pool_codes_by_slice: mapping } : {};
    })(),
  });

  /**
   * Phase 18 Round 2B — Static Offer listing-duration checkout helper.
   * Called by the wizard's `submit()` when a static_offer campaign is
   * created with an explicit duration. Free 7-day tier resolves via
   * `/listing/purchase`; paid tiers debit the wallet via
   * `/listing/pay-with-wallet`. If balance is insufficient the caller
   * catches the 402 and shows a warning toast — the campaign itself
   * still gets created; the vendor can renew from the campaign detail
   * page later.
   */
  const purchaseListingDuration = async (campaignId, durationDays) => {
    const dd = Number(durationDays);
    if (dd === 7) {
      await purchaseListingFreeTier({ campaignId });
      return;
    }
    // Paid tier — wallet debit.
    await payListingWithWallet({
      campaignId,
      durationDays: dd,
    });
  };


  const submit = async () => {
    // Client-side guard for per-slice redirect URLs — mirror the
    // server rule so the vendor sees a clear inline error before we
    // even round-trip. Server enforces the same rule.
    const badSliceIdx = form.slices.findIndex((s) => {
      const raw = typeof s.redirect_url === "string" ? s.redirect_url.trim() : "";
      return raw.length > 0 && !/^https?:\/\//i.test(raw);
    });
    if (badSliceIdx >= 0) {
      const s = form.slices[badSliceIdx];
      const label = (s.label || s.gift_name || `Prize ${badSliceIdx + 1}`).toString().trim();
      toast.error(
        `Redirect link for "${label}" must start with http:// or https://`,
      );
      return;
    }
    setBusy(true);
    try {
      let campaign;
      if (editing) {
        const r = await api.updateCampaign(id, buildPayload());
        campaign = r.data;
      } else if (adminForVendor) {
        // A3 — admin creates campaign on behalf of the target vendor.
        // Backend re-runs the exact same handler with target_vendor_id
        // so billing/trial rules never diverge from the vendor path.
        const r = await api.adminCreateCampaignForVendor(adminForVendor, buildPayload());
        campaign = r.data;
      } else {
        const r = await api.createCampaign(buildPayload());
        campaign = r.data;
      }
      // If the new campaign is stuck in pending_payment, jump straight to
      // the dedicated checkout page — the vendor gets to see the invoice
      // breakdown (base + 18% GST + total) before paying.
      if (!editing && campaign?.status === "pending_payment") {
        toast.info("Campaign saved — complete payment to activate.");
        navigate(`/vendor/checkout/${campaign.id}`);
        return;
      }
      // 02/08 — Static Offer listing-duration purchase. Only fires
      // for freshly-created static_offer campaigns that opted into
      // Explore listing. Free tier (7d) resolves synchronously; paid
      // tiers pop Razorpay Checkout inline.
      if (
        !editing && !adminForVendor &&
        campaign?.id &&
        form.game_type === "static_offer" &&
        form.listed_publicly &&
        Number(form.listing_duration_days) > 0
      ) {
        try {
          await purchaseListingDuration(campaign.id, Number(form.listing_duration_days));
        } catch (le) {
          // Non-fatal — the campaign itself was created successfully,
          // it just didn't get its extended listing yet. Vendor can
          // renew from the detail page.
          console.warn("listing purchase failed:", le);
          toast.warning("Campaign created, but listing upgrade didn't complete. You can renew from the campaign detail page.");
        }
      }
      // Generate QR
      try {
        const q = await api.generateQr(campaign.id);
        setLaunched({ qr_code_url: q.data.qr_code_url, campaign_url: q.data.campaign_url, id: campaign.id });
      } catch {
        setLaunched({ qr_code_url: null, campaign_url: campaign.campaign_url, id: campaign.id });
      }
      toast.success(editing ? "Campaign updated" : "Campaign launched!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  // ── Game-type terminology map ──────────────────────────────────
  // Single source of truth for every vendor-facing string that has to
  // change with the game type. Add one entry per new game_type and
  // every downstream label updates automatically. Keeps a real
  // three-way lookup instead of `game_type === "scratch_card" ? … : …`
  // binary checks so a future fourth mode doesn't silently regress.
  //
  // NOTE: declared BEFORE `STEPS` so the step-nav label (which reads
  // gameCopy.stepLabel) doesn't hit a temporal-dead-zone reference
  // error on component mount. Regression fix — see bug report on
  // "blank black page loading /vendor/campaigns/new".
  const gameCopy = useMemo(() => {
    const t = form.game_type || "spin_wheel";
    const map = {
      spin_wheel: {
        stepLabel: "Wheel",
        configHeading: "Wheel configuration",
        winTitle: "SPIN & WIN",
        ctaLabel: "Spin Now",
        actionVerb: "spin",
        actionGerund: "spinning",
        beforeAction: "before spin",
        limitLabel: "Spin limit",
        limitOncePer: "Once per phone",
        sliceNoun: "slice",
        addSliceLabel: "Add slice",
      },
      scratch_card: {
        stepLabel: "Scratch Card",
        configHeading: "Scratch Card configuration",
        winTitle: "SCRATCH & WIN",
        ctaLabel: "Scratch To Win",
        actionVerb: "reveal",
        actionGerund: "revealing",
        beforeAction: "before revealing",
        limitLabel: "Scratch limit",
        limitOncePer: "Once per phone",
        sliceNoun: "offer",
        addSliceLabel: "Add offer",
      },
      static_offer: {
        stepLabel: "Offer",
        configHeading: "Offer configuration",
        winTitle: "CLAIM YOUR OFFER",
        ctaLabel: "Get This Offer",
        actionVerb: "claim",
        actionGerund: "claiming",
        beforeAction: "before claiming",
        limitLabel: "Claim limit",
        limitOncePer: "Once per phone",
        sliceNoun: "prize",
        addSliceLabel: "Add prize",
      },
    };
    return map[t] || map.spin_wheel;
  }, [form.game_type]);

  const STEPS = [
    { id: 1, label: "Basics" },
    // Step 2's short pill label adapts to the picked game so the
    // progress bar tells the vendor which mechanic they're configuring.
    { id: 2, label: gameCopy.stepLabel },
    { id: 3, label: "Form" },
    { id: 4, label: "Coupon" },
    { id: 5, label: "Review" },
  ];

  // Pool-mode mismatch detector — shared by canNext (Step 2) and
  // canSubmit (Step 5). A slice fails only when the vendor HAS
  // uploaded a file AND the parsed count doesn't equal the declared
  // per-slice quantity. Pool slices with no upload attached are still
  // allowed to progress; the vendor can top up via the append-upload
  // path after creation. Server re-runs the same check (see
  // routes/campaigns.py::create_campaign) as the mandatory safety net.
  const poolMismatchIndex = useMemo(() => {
    for (let i = 0; i < form.slices.length; i += 1) {
      const s = form.slices[i];
      const mode = (s.coupon_mode || "auto").toLowerCase();
      if (mode !== "pool") continue;
      if (s.is_better_luck) continue;
      const parsed = Array.isArray(s._pending_pool_codes) ? s._pending_pool_codes : [];
      if (parsed.length === 0) continue;
      const expected = form.game_type === "static_offer"
        ? maxParticipants
        : sliceQty(s.percentage);
      if (parsed.length !== expected) return i;
    }
    return -1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.slices, form.game_type, maxParticipants]);

  const canNext = () => {
    if (step === 1) return form.name.trim().length >= 2 && form.theme && maxParticipants >= 1;
    if (step === 2) {
      // Block progression when ANY pool slice has an uploaded file
      // that doesn't match its declared quantity — the red banner
      // right below the upload control tells the vendor exactly which
      // slice + expected/actual counts.
      if (poolMismatchIndex >= 0) return false;
      // Static offers ship exactly one 100% slice — the multi-slice
      // sum-to-100 check does not apply. We still require the offer
      // title + gift name to be filled so the /offers/{id} page has
      // something meaningful to render.
      if (form.game_type === "static_offer") {
        const only = form.slices[0] || {};
        const label = (only.label || "").trim();
        const gift = (only.gift_name || only.label || "").trim();
        // Coupon-mode-specific requirements (mirrors server rules):
        //   auto   → nothing extra
        //   single → single_code must be non-empty
        //   pool   → uploading a file is OPTIONAL (vendor may top up
        //            later); if they DID upload, the exact-count
        //            check above governs progression.
        const mode = (only.coupon_mode || "auto").toLowerCase();
        if (mode === "single" && !(only.single_code || "").trim()) return false;
        return Boolean(label && gift);
      }
      return form.slices.length >= 2 && percentIsExact;
    }
    return true;
  };

  // Submit-time validity. Static offers already satisfied their per-
  // slice checks in canNext(), so at the Review step we only need the
  // basics. Spin/scratch still need the 100% sum + ≥2 slices.
  const canSubmit = useMemo(() => {
    // Belt-and-braces: never let a Submit fire while a pool file is
    // mismatched, even if the vendor jumped straight to Step 5 via a
    // stale URL. The wizard's canNext blocks earlier — this is just
    // the final gate before we POST.
    if (poolMismatchIndex >= 0) return false;
    if (form.game_type === "static_offer") {
      const only = form.slices[0] || {};
      const label = (only.label || "").trim();
      const gift = (only.gift_name || only.label || "").trim();
      const mode = (only.coupon_mode || "auto").toLowerCase();
      if (mode === "single" && !(only.single_code || "").trim()) return false;
      return Boolean(label && gift);
    }
    return percentIsExact && form.slices.length >= 2;
  }, [form.game_type, form.slices, percentIsExact, poolMismatchIndex]);

  /* ── Live-preview theme composition ──────────────────────────────
     Mirrors the exact resolution PlayPage does so the vendor sees the
     same rendered result while configuring:
       1. wizard `category_theme` (if picked)                      →
       2. slugify(vendor.business_type) auto-detect                →
       3. "other" fallback (CityDooh gold on ink)
     `theme_intensity` and `manual_theme_variant` layer on top; the
     brand colors run through SafeColors so slice labels stay legible
     against the resolved category surface. */
  const previewTheme = useMemo(() => {
    const catSlug =
      form.category_theme ||
      slugifyBusinessType(vendorBusinessType) ||
      "other";
    const palette = normalizePalette({
      primary: form.primary_color,
      secondary: form.secondary_color,
      theme: { slicePrimary: "#F5A800", sliceSecondary: "#0A0A0A" },
    });
    return composeTheme({
      categorySlug: catSlug,
      intensity: form.theme_intensity || "branded",
      manualVariant: form.manual_theme_variant || undefined,
      safePalette: palette,
    });
  }, [
    form.category_theme,
    form.theme_intensity,
    form.manual_theme_variant,
    form.primary_color,
    form.secondary_color,
    vendorBusinessType,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/vendor/campaigns" className="text-sm text-muted-foreground hover:underline">
            ← Back to campaigns
          </Link>
          <h1 className="font-heading text-3xl font-semibold mt-1">
            {editing ? "Edit campaign" : "Create campaign"}
          </h1>
        </div>
      </div>

      {/* Billing banner (create-only) */}
      {!editing && billing && <BillingBanner billing={billing} />}

      {/* Stepper — horizontal-scroll strip on mobile so a 4th "Coupon"
          step never gets clipped off the right edge at 360-430px. On
          md+ the flex-wrap keeps the row visually intact without
          triggering a scrollbar. `snap-x` + `snap-start` centers whichever
          step the user last tapped, so the current step is always in
          the viewport after selection. */}
      <div
        className="-mx-4 sm:mx-0 overflow-x-auto md:overflow-visible snap-x snap-mandatory"
        data-testid="campaign-wizard-stepper-scroll"
      >
        <div
          className="inline-flex md:flex md:flex-wrap items-center gap-2 px-4 sm:px-0 min-w-max md:min-w-0"
          data-testid="campaign-wizard-stepper"
        >
          {STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <button
                onClick={() => setStep(s.id)}
                className={`snap-start shrink-0 min-h-11 flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border ${
                  step === s.id
                    ? "bg-primary text-primary-foreground border-primary"
                    : step > s.id
                    ? "bg-accent text-accent-foreground border-accent"
                    : "bg-card border-border text-muted-foreground"
                }`}
                data-testid={`campaign-wizard-step-${s.id}`}
              >
                <span className="tabular-nums">{s.id}</span>
                <span className="whitespace-nowrap">{s.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <div
                  className="h-px w-4 bg-border shrink-0 hidden md:block"
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="p-6 lg:col-span-7 shadow-[var(--shadow-card)]">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-semibold">Campaign basics</h2>
              <div className="space-y-1.5">
                <Label>Campaign name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  data-testid="wiz-campaign-name"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label>Game type</Label>
                  <Select
                    value={form.game_type}
                    onValueChange={(v) => {
                      // Static offer: collapse to a single 100% slice so
                      // the vendor doesn't have to manually delete the
                      // default seed slices. Spin/Scratch keep their
                      // existing multi-slice defaults unchanged.
                      setForm((s) => {
                        if (v === "static_offer") {
                          const first = s.slices[0] || {
                            label: "New Offer",
                            gift_name: "New Offer",
                            color_hex: SLICE_PALETTE[0],
                            is_better_luck: false,
                            image_url: "",
                            terms: "",
                            redirect_url: "",
                            coupon_mode: "auto",
                          };
                          return {
                            ...s,
                            game_type: v,
                            slices: [{ ...first, percentage: 100, is_better_luck: false }],
                          };
                        }
                        // Coming back from static_offer → restore a
                        // sensible default 2-slice starter.
                        if (s.game_type === "static_offer" && v !== "static_offer") {
                          return { ...s, game_type: v, slices: defaultSlices() };
                        }
                        return { ...s, game_type: v };
                      });
                    }}
                  >
                    <SelectTrigger data-testid="wiz-game-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spin_wheel">Spin Wheel</SelectItem>
                      <SelectItem value="scratch_card">Scratch Card</SelectItem>
                      <SelectItem value="static_offer">Static Offer</SelectItem>
                      <SelectItem value="slot_machine" disabled>Slot Machine (soon)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Maximum participants</Label>
                  <Input
                    type="number"
                    min={1}
                    max={1000000}
                    step={50}
                    value={form.max_participants}
                    onChange={(e) =>
                      update(
                        "max_participants",
                        e.target.value.replace(/[^0-9]/g, "")
                      )
                    }
                    placeholder="500"
                    data-testid="wiz-max-participants"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Total prize inventory across all slices.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>Expires on</Label>
                  <Input
                    type="date"
                    value={form.expires_at}
                    onChange={(e) => update("expires_at", e.target.value)}
                    data-testid="wiz-expires"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Campaign auto-closes on this date.
                  </p>
                </div>
              </div>
              {/*
                Campaign city — Cities Phase-1. Defaults to the vendor's
                profile city (seeded from `api.me()` above) but is
                independently editable per campaign so a vendor can, e.g.,
                run a Guntur-only campaign from a Visakhapatnam HQ.
                Filtered by the Explore feed and the homepage hero.
              */}
              <div className="space-y-1.5">
                <Label>Campaign city</Label>
                <CitySelect
                  value={form.campaign_city}
                  onChange={(name) => update("campaign_city", name)}
                  placeholder="Select the city this campaign runs in…"
                  testIdPrefix="wiz-city"
                />
                <p className="text-[11px] text-muted-foreground">
                  Determines where this campaign appears on Explore. Defaults to your profile city.
                </p>
              </div>
              {/*
                NOTE — the legacy "Theme" swatch grid (citydooh / christmas /
                diwali / royal / onyx / …) has been removed from Campaign
                Basics on purpose. Visual theming is now driven from the
                Wheel step by Category Theme + Intensity + Manual Swatch +
                Seasonal Accent. `form.theme` is preserved in state (default
                "citydooh") only for backward compatibility with the
                confetti-colour mapping in `themeConfettiColors()` used on
                the win popup — see PlayPage.jsx.
              */}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-semibold">
                {gameCopy.configHeading}
              </h2>
              {form.game_type === "static_offer" ? (
                <p className="text-sm text-muted-foreground -mt-3">
                  Static offers are single-prize instant claims — no wheel or scratch mechanic.
                  Configure the single prize below.
                </p>
              ) : null}
              {form.game_type !== "static_offer" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>
                    {form.game_type === "scratch_card"
                      ? `Offers on the card (${form.slices.length})`
                      : `Slices (${form.slices.length})`}
                  </Label>
                  <Slider
                    min={2}
                    max={12}
                    step={1}
                    value={[form.slices.length]}
                    onValueChange={([v]) => setSliceCount(v)}
                    data-testid="wiz-slice-count"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Brand colors</Label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={form.primary_color}
                      onChange={(e) => update("primary_color", e.target.value)}
                      className="h-10 w-12 rounded border border-border"
                      data-testid="wiz-primary-color"
                    />
                    <input
                      type="color"
                      value={form.secondary_color}
                      onChange={(e) => update("secondary_color", e.target.value)}
                      className="h-10 w-12 rounded border border-border"
                      data-testid="wiz-secondary-color"
                    />
                  </div>
                </div>
              </div>
              )}

              <div className="space-y-1.5">
                <Label>Logo (shown at center)</Label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sm px-3 py-2 rounded-lg border border-border hover:bg-secondary">
                    <Upload className="h-4 w-4" />
                    Upload logo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onUpload(e.target.files?.[0], { kind: "logo" })}
                      data-testid="wiz-logo-upload"
                    />
                  </label>
                  {form.logo_url && (
                    <img src={assetUrl(form.logo_url)} alt="logo" className="h-10 w-10 rounded-full object-cover ring-1 ring-border" />
                  )}
                </div>
              </div>

              {/* ── Phase 2 — Category Theme picker ─────────────────────
                  Vendors override the auto-detected theme (from their
                  vendor.business_type) with any of the 17 category
                  presets. Intensity dials decoration up/down. Manual
                  variant applies a swatch override on top. All code-
                  driven — zero AI, zero external API call, zero cost. */}
              <div
                className="space-y-3 rounded-xl border border-border p-4 bg-secondary/30"
                data-testid="wiz-category-theme-panel"
              >
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">
                    Category Theme <span className="text-muted-foreground font-normal">(auto-detected if left blank)</span>
                  </Label>
                </div>
                <Select
                  value={form.category_theme || "__auto__"}
                  onValueChange={(v) => update("category_theme", v === "__auto__" ? "" : v)}
                >
                  <SelectTrigger data-testid="wiz-category-theme-select">
                    <SelectValue placeholder="Auto-detect from business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__auto__">Auto-detect from business type</SelectItem>
                    <SelectItem value="cafe">Cafe</SelectItem>
                    <SelectItem value="bakery_sweets">Bakery & Sweets</SelectItem>
                    <SelectItem value="retail_store">Retail Store</SelectItem>
                    <SelectItem value="supermarket_grocery">Supermarket & Grocery</SelectItem>
                    <SelectItem value="clothing_fashion">Clothing & Fashion</SelectItem>
                    <SelectItem value="footwear">Footwear</SelectItem>
                    <SelectItem value="jewellery">Jewellery</SelectItem>
                    <SelectItem value="electronics_mobiles">Electronics & Mobiles</SelectItem>
                    <SelectItem value="salon_spa">Salon & Spa</SelectItem>
                    <SelectItem value="pharmacy_healthcare">Pharmacy & Healthcare</SelectItem>
                    <SelectItem value="optical_eyewear">Optical & Eyewear</SelectItem>
                    <SelectItem value="furniture_home_decor">Furniture & Home Decor</SelectItem>
                    <SelectItem value="automobile">Automobile</SelectItem>
                    <SelectItem value="hotel_hospitality">Hotel & Hospitality</SelectItem>
                    <SelectItem value="gym_fitness">Gym & Fitness</SelectItem>
                    <SelectItem value="education_coaching">Education & Coaching</SelectItem>
                    <SelectItem value="other">Other (CityDooh Gold)</SelectItem>
                  </SelectContent>
                </Select>

                <div>
                  <Label className="text-sm font-semibold">Theme Intensity</Label>
                  <div className="mt-2 grid grid-cols-3 gap-2" data-testid="wiz-theme-intensity">
                    {["minimal", "branded", "immersive"].map((lvl) => (
                      <button
                        key={lvl}
                        type="button"
                        onClick={() => update("theme_intensity", lvl)}
                        className={`rounded-lg border px-3 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                          form.theme_intensity === lvl
                            ? "bg-[#F5A800] border-[#F5A800] text-black"
                            : "bg-transparent border-border text-foreground hover:bg-secondary"
                        }`}
                        data-testid={`wiz-intensity-${lvl}`}
                      >
                        {lvl}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Default: Branded. Minimal = subtle background tint only. Immersive = maximum decoration.
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-semibold">
                    Manual Swatch Variant <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <div className="mt-2 grid grid-cols-6 gap-2" data-testid="wiz-manual-variant">
                    <button
                      type="button"
                      onClick={() => update("manual_theme_variant", "")}
                      className={`rounded-lg border px-2 py-2 text-[10px] uppercase font-semibold ${
                        !form.manual_theme_variant ? "border-[#F5A800] bg-[#F5A800]/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      None
                    </button>
                    {[
                      { key: "aurora",   swatch: "#5a8cff" },
                      { key: "ember",    swatch: "#e26a45" },
                      { key: "graphite", swatch: "#2a2a2a" },
                      { key: "sapphire", swatch: "#8a5ac8" },
                      { key: "sunrise",  swatch: "#f4b840" },
                    ].map((v) => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => update("manual_theme_variant", v.key)}
                        className={`rounded-lg border px-1 py-2 flex flex-col items-center gap-1 ${
                          form.manual_theme_variant === v.key ? "border-[#F5A800]" : "border-border"
                        }`}
                        data-testid={`wiz-variant-${v.key}`}
                      >
                        <span className="block h-4 w-4 rounded-full border border-black/40" style={{ backgroundColor: v.swatch }} />
                        <span className="text-[9px] font-semibold uppercase">{v.key}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Seasonal Accent ────────────────────────────
                    Optional festive overlay applied on top of the
                    category theme. Pure CSS/SVG, zero AI. Data-only
                    field for now — visual rendering will layer in
                    subsequent passes without any wizard changes. */}
                <div>
                  <Label className="text-sm font-semibold">
                    Seasonal Accent <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Select
                    value={form.seasonal_accent || "__none__"}
                    onValueChange={(v) => update("seasonal_accent", v === "__none__" ? "" : v)}
                  >
                    <SelectTrigger className="mt-2" data-testid="wiz-seasonal-accent-select">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      <SelectItem value="christmas">Christmas</SelectItem>
                      <SelectItem value="diwali">Diwali</SelectItem>
                      <SelectItem value="newyear">New Year</SelectItem>
                      <SelectItem value="valentine">Valentine</SelectItem>
                      <SelectItem value="citydooh">CityDooh</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground mt-1.5">
                    Layers festive confetti + subtle decorations on top of the category theme. Leave blank for a clean look.
                  </p>
                </div>

                {/* Live preview link — opens the actual /play page in a
                    new tab so vendors can see the theme applied in real
                    context (mobile viewport if they resize). This is
                    the "live preview" required by the brief without
                    building a separate render surface. */}
                {editing && (
                  <a
                    href={`/play/${id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F5A800] hover:underline"
                    data-testid="wiz-theme-preview-link"
                  >
                    Preview live on your campaign page →
                  </a>
                )}
              </div>

              {/* Featured Image — landscape banner shown on the /play
                  page as a 16:9 hero between the title and the wheel,
                  and reused as the main image drawn onto the win-popup
                  share card. Optional; falls back to no banner if not
                  set. UI copy uses "Featured Image" everywhere so the
                  vendor sees consistent labelling. */}
              <div className="space-y-1.5">
                <Label>Featured Image (1920×1080 recommended)</Label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer text-sm px-3 py-2 rounded-lg border border-border hover:bg-secondary">
                    <Upload className="h-4 w-4" />
                    Upload image
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onUpload(e.target.files?.[0], { kind: "thumbnail" })}
                      data-testid="wiz-featured-image-upload"
                    />
                  </label>
                  {form.thumbnail_url && (
                    <div className="relative">
                      <img
                        src={assetUrl(form.thumbnail_url)}
                        alt="Featured"
                        className="h-14 w-24 rounded-lg object-cover ring-1 ring-border"
                        data-testid="wiz-featured-image-preview"
                      />
                      <button
                        type="button"
                        className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-black text-white text-[10px] leading-none"
                        onClick={() => update("thumbnail_url", "")}
                        aria-label="Remove featured image"
                      >×</button>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Shown as a 16:9 banner on the play page and on the
                  shareable win card. Wide landscape works best.
                </p>
              </div>

              {form.game_type !== "static_offer" && getThemeMeta(form.theme).preview && (
                <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div className="pr-4">
                    <div className="font-medium text-sm">
                      Match {getThemeMeta(form.theme).label} theme style
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Auto-style the wheel with alternating theme color & white slices,
                      themed center & ring. Turn off to use your own slice colors below.
                    </div>
                  </div>
                  <Switch
                    checked={!!form.use_theme_style}
                    onCheckedChange={(v) => update("use_theme_style", v)}
                    data-testid="wiz-use-theme-style"
                  />
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  {/* Section header adapts to the game type — same slice
                      data model underneath, just clearer terminology
                      for vendors building scratch cards or static offers. */}
                  <Label>
                    {form.game_type === "static_offer"
                      ? "The offer"
                      : form.game_type === "scratch_card"
                      ? "Offers on the card · % share (sums to 100%)"
                      : "Slices · % share (sums to 100%)"}
                  </Label>
                  {form.game_type !== "static_offer" && (
                    <div
                      className="text-xs tabular-nums font-semibold px-2 py-1 rounded-md"
                      style={{ color: quantityStatus.color, backgroundColor: `${quantityStatus.color}1a` }}
                      data-testid="wiz-quantity-status"
                    >
                      Total: {Math.round(totalPct * 100) / 100}% / 100% • {quantityStatus.msg}
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground -mt-1">
                  {form.game_type === "static_offer" ? (
                    <>
                      Total quantity available: <span className="font-medium text-foreground tabular-nums">{maxParticipants}</span>.
                      Each customer who claims gets one coupon until the offer is fully claimed.
                    </>
                  ) : (
                    <>
                      Each {form.game_type === "scratch_card" ? "offer" : "slice"} receives{" "}
                      <span className="font-medium text-foreground tabular-nums">% × {maxParticipants}</span> prizes.
                      Total inventory at launch: <span className="font-medium text-foreground tabular-nums">{totalQty}</span> / {maxParticipants}.
                    </>
                  )}
                </div>
                <div className="space-y-2">
                  {form.slices.map((s, i) => {
                    const pct = Math.max(0, parseFloat(s.percentage || 0));
                    const qty = sliceQty(pct);
                    return (
                      <div key={i} className="rounded-lg border border-border/60 p-2 space-y-2" data-testid={`wiz-slice-block-${i}`}>
                        <div className="grid grid-cols-12 gap-2 items-center" data-testid={`wiz-slice-row-${i}`}>
                        <input
                          type="color"
                          className="col-span-1 h-10 w-10 rounded border border-border"
                          value={s.color_hex}
                          onChange={(e) => updateSlice(i, "color_hex", e.target.value)}
                        />
                        <Input
                          className={form.game_type === "static_offer" ? "col-span-4" : "col-span-3"}
                          placeholder={form.game_type === "static_offer"
                            ? "Offer title (e.g. Buy 1 Get 1 Pizza)"
                            : form.game_type === "scratch_card" ? "Offer title" : "Label"}
                          value={s.label}
                          onChange={(e) => updateSlice(i, "label", e.target.value)}
                        />
                        <Input
                          className={form.game_type === "static_offer" ? "col-span-6" : "col-span-3"}
                          placeholder={form.game_type === "scratch_card" ? "Reward (e.g. Free coffee)" : "Gift name"}
                          value={s.gift_name}
                          onChange={(e) => updateSlice(i, "gift_name", e.target.value)}
                        />
                        {form.game_type !== "static_offer" && (
                          <>
                        <div className="col-span-2 relative">
                          <Input
                            type="number"
                            step="0.5"
                            min={0}
                            max={100}
                            className="pr-7"
                            placeholder="%"
                            value={s.percentage}
                            onChange={(e) =>
                              updateSlice(
                                i,
                                "percentage",
                                e.target.value.replace(/[^0-9.]/g, "")
                              )
                            }
                            data-testid={`wiz-slice-pct-${i}`}
                          />
                          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                        </div>
                        <div
                          className="col-span-1 text-xs text-center tabular-nums font-medium text-muted-foreground"
                          title={`${pct}% of ${maxParticipants}`}
                          data-testid={`wiz-slice-qty-${i}`}
                        >
                          ={qty}
                        </div>
                        <label className="col-span-1 text-xs flex items-center gap-1 justify-center" title="Better luck (no coupon)">
                          <input
                            type="checkbox"
                            checked={!!s.is_better_luck}
                            onChange={(e) => updateSlice(i, "is_better_luck", e.target.checked)}
                          />
                          L
                        </label>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className={`col-span-1 ${form.game_type === "static_offer" ? "hidden" : ""}`}
                          onClick={() => removeSlice(i)}
                          disabled={form.slices.length <= 2}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        </div>
                        {/* Terms & Conditions — optional per-slice fine print, up to 500 chars.
                            Rendered under the coupon code on the win popup. */}
                        <div className="pl-12 pr-2">
                          <Textarea
                            placeholder={`Terms & Conditions for "${s.label || `Prize ${i + 1}`}" (optional, max 500 chars). Shown to the winner alongside their coupon.`}
                            value={s.terms || ""}
                            onChange={(e) => updateSlice(i, "terms", e.target.value.slice(0, 500))}
                            maxLength={500}
                            rows={2}
                            className="text-xs resize-none"
                            data-testid={`wiz-slice-terms-${i}`}
                          />
                          <div className="mt-0.5 text-[10px] text-right text-muted-foreground tabular-nums">
                            {(s.terms || "").length}/500
                          </div>
                        </div>
                        {/* Per-slice post-spin Redirect URL (optional).
                            Overrides the platform-default post_spin_redirect_url
                            when a winner of THIS slice taps Continue / Close
                            / lets the 15-second timer expire, and is also
                            the destination of the "View my Prize" button
                            in the win-confirmation email. Must be a full
                            http(s) URL — validated server-side too.
                            Better-luck slices don't award a coupon and
                            therefore never trigger any redirect, so we
                            hide the field there to keep the wizard tidy. */}
                        {!s.is_better_luck && (() => {
                          const rawUrl = s.redirect_url || "";
                          const trimmed = rawUrl.trim();
                          const clientInvalid =
                            trimmed.length > 0 &&
                            !/^https?:\/\//i.test(trimmed);
                          return (
                            <div className="pl-12 pr-2">
                              <Input
                                type="url"
                                inputMode="url"
                                placeholder='Redirect Link (optional) — e.g. https://link.amazon/xyz'
                                value={rawUrl}
                                onChange={(e) => updateSlice(i, "redirect_url", e.target.value)}
                                className={`text-xs ${clientInvalid ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                                data-testid={`wiz-slice-redirect-${i}`}
                                aria-invalid={clientInvalid || undefined}
                              />
                              <p
                                className={`mt-0.5 text-[10px] leading-snug ${
                                  clientInvalid ? "text-red-600" : "text-muted-foreground"
                                }`}
                              >
                                {clientInvalid
                                  ? "Please enter a full URL starting with http:// or https://"
                                  : "Where should winners of this offer be sent after claiming their prize? Leave blank to use your platform's default redirect."}
                              </p>
                            </div>
                          );
                        })()}
                        {/* Coupon Source — only for real-prize slices.
                            Better-luck slices don't award coupons, so
                            hiding this here keeps the wizard focused.
                            `expectedCount` is the absolute per-slice
                            prize quantity — used by pool mode to
                            enforce the exact-count check without a
                            round-trip. For static_offer the only slice
                            takes the whole max_participants. */}
                        {!s.is_better_luck && (
                          <div className="pl-12 pr-2">
                            <CouponSourceField
                              slice={s}
                              index={i}
                              campaignId={id}
                              expectedCount={
                                form.game_type === "static_offer"
                                  ? maxParticipants
                                  : sliceQty(s.percentage)
                              }
                              onChange={(k, v) => updateSlice(i, k, v)}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addSlice}
                  disabled={form.slices.length >= 12}
                  data-testid="wiz-add-slice"
                  className={form.game_type === "static_offer" ? "hidden" : ""}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add {form.game_type === "scratch_card" ? "offer" : "slice"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {form.game_type === "static_offer"
                    ? "Set the offer title, image, terms, redirect link, and coupon source below. No slices or percentages — the single prize is always awarded on claim."
                    : 'L = "Better luck next time" (no coupon). Column "=N" is the actual number of prizes that slice will award.'}
                </p>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-semibold">Lead form</h2>
              {/* ── Explore listing toggle ─────────────────────────
                  Wired to the campaign document's `listed_publicly`
                  field which /api/public/campaigns filters on. OFF by
                  default (privacy-first) — direct /play or /offers
                  links keep working either way. */}
              <div className="flex items-start justify-between p-3 border border-border rounded-lg">
                <div className="pr-4">
                  <div className="font-medium">List on Explore</div>
                  <div className="text-xs text-muted-foreground">
                    When ON, this campaign appears in the public{" "}
                    <span className="font-medium">/explore</span> feed so
                    nearby customers can discover it. When OFF, it&apos;s only
                    reachable via the direct link/QR you share. Change any
                    time — nothing else about the campaign changes.
                  </div>
                </div>
                <Switch
                  checked={!!form.listed_publicly}
                  onCheckedChange={(v) => update("listed_publicly", v)}
                  data-testid="wiz-listed-publicly"
                />
              </div>

              {/* 02/08 — Static Offer listing-duration picker. Only
                  surfaces for game_type=='static_offer' AND when the
                  vendor has flipped List-on-Explore ON. Spin/Scratch
                  stay free + indefinite; this is only for the Static
                  Offer path per the spec. */}
              {form.game_type === "static_offer" && form.listed_publicly && !editing && (
                <div className="p-3 border-2 border-[#F5A800]/40 bg-[#F5A800]/5 rounded-lg space-y-2" data-testid="wiz-listing-duration">
                  <div className="font-medium">Listing duration</div>
                  <div className="text-xs text-muted-foreground">
                    Choose how long this Static Offer stays visible on Explore. Separate from the ₹499 campaign fee. Renewable later from the campaign detail page.
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    {[
                      { d: 7, label: "7 days", price: "Free" },
                      { d: 15, label: "15 days", price: "₹99 + GST" },
                      { d: 30, label: "30 days", price: "₹149 + GST" },
                    ].map((opt) => {
                      const active = Number(form.listing_duration_days) === opt.d;
                      return (
                        <button
                          type="button"
                          key={opt.d}
                          onClick={() => update("listing_duration_days", opt.d)}
                          className={`rounded-lg border-2 p-2.5 text-center transition-colors ${active ? "border-[#F5A800] bg-[#F5A800]/20" : "border-border hover:border-[#F5A800]/60"}`}
                          data-testid={`wiz-listing-${opt.d}d`}
                        >
                          <div className="font-heading text-lg font-bold">{opt.label}</div>
                          <div className="text-xs text-muted-foreground">{opt.price}</div>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Paid tiers open Razorpay Checkout right after you launch. On success your offer goes live for the chosen window; if you skip it the campaign is still created and listed for the free 7 days.
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="pr-4">
                  <div className="font-medium">Require email</div>
                  <div className="text-xs text-muted-foreground">Customer must enter an email to {gameCopy.actionVerb}.</div>
                </div>
                <Switch
                  checked={form.require_email || form.require_email_otp}
                  disabled={form.require_email_otp}
                  onCheckedChange={(v) => update("require_email", v)}
                  data-testid="wiz-require-email"
                />
              </div>
              <div className="flex items-start justify-between p-3 border border-border rounded-lg">
                <div className="pr-4">
                  <div className="font-medium">Require email OTP verification {gameCopy.beforeAction}</div>
                  <div className="text-xs text-muted-foreground">
                    Customer must verify their email address with a 6-digit code {gameCopy.beforeAction}.
                    Enabling this forces the email field on.
                  </div>
                </div>
                <Switch
                  checked={!!form.require_email_otp}
                  onCheckedChange={(v) =>
                    setForm((s) => ({
                      ...s,
                      require_email_otp: v,
                      // OTP requires the email field, so flip it on automatically.
                      require_email: v ? true : s.require_email,
                    }))
                  }
                  data-testid="wiz-require-email-otp"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{gameCopy.limitLabel}</Label>
                <Select value={form.spin_limit} onValueChange={(v) => update("spin_limit", v)}>
                  <SelectTrigger data-testid="wiz-spin-limit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">{gameCopy.limitOncePer}</SelectItem>
                    <SelectItem value="cooldown_24h">Once per 24 hours</SelectItem>
                    <SelectItem value="unlimited">Unlimited</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* ── Sound picker — swaps in Scratch Sound when the
                  campaign is scratch_card, keeps Spin Sound otherwise.
                  Both are client-synth'd via Web Audio; the backend
                  persists whichever the vendor picked. Static offers
                  play no reveal animation, so no sound picker. */}
              {form.game_type === "static_offer" ? null : form.game_type === "scratch_card" ? (
                <div className="space-y-1.5">
                  <Label>Scratch Sound</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="wiz-scratch-sound">
                    {[
                      { id: "classic", label: "Classic", hint: "Soft, warm" },
                      { id: "shimmer", label: "Shimmer", hint: "Bright, sparkly" },
                      { id: "none",    label: "None",    hint: "Silent" },
                    ].map((opt) => {
                      const selected = form.scratch_sound === opt.id;
                      return (
                        <div
                          key={opt.id}
                          className={`rounded-md border-2 p-2 text-left transition ${
                            selected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/60"
                          }`}
                          data-testid={`wiz-scratch-sound-option-${opt.id}`}
                        >
                          <button
                            type="button"
                            onClick={() => update("scratch_sound", opt.id)}
                            className="w-full text-left"
                          >
                            <div className="text-sm font-semibold">{opt.label}</div>
                            <div className="text-[11px] text-muted-foreground">{opt.hint}</div>
                          </button>
                          {opt.id !== "none" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                // Preview: play a ~500ms scratch burst + chime
                                import("@/lib/scratchSound").then((m) => {
                                  const ctl = m.playScratchLoop(opt.id);
                                  setTimeout(() => { ctl.stop?.(); m.playRevealChime(opt.id); }, 500);
                                });
                              }}
                              className="mt-1 w-full text-[11px] text-primary font-semibold hover:underline"
                              data-testid={`wiz-scratch-sound-preview-${opt.id}`}
                            >
                              ▶ Preview
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
              <div className="space-y-1.5">
                <Label>Spin Sound</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="wiz-spin-sound">
                  {[
                    { id: "classic", label: "Classic", hint: "Warm chime" },
                    { id: "arcade", label: "Arcade", hint: "Retro blip" },
                    { id: "casino", label: "Casino", hint: "Bell tone" },
                    { id: "none", label: "None", hint: "Silent" },
                  ].map((opt) => {
                    const selected = form.spin_sound === opt.id;
                    return (
                      <div
                        key={opt.id}
                        className={`rounded-md border-2 p-2 text-left transition ${
                          selected
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/60"
                        }`}
                        data-testid={`wiz-spin-sound-option-${opt.id}`}
                      >
                        <button
                          type="button"
                          onClick={() => update("spin_sound", opt.id)}
                          className="w-full text-left"
                        >
                          <div className="text-sm font-semibold">{opt.label}</div>
                          <div className="text-[11px] text-muted-foreground">{opt.hint}</div>
                        </button>
                        {opt.id !== "none" && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              // Lazy import so the wizard bundle doesn't
                              // eagerly reach for Web Audio APIs at page load.
                              import("@/lib/spinSound").then((m) => m.previewSpinSound(opt.id));
                            }}
                            className="mt-1 w-full text-[11px] text-primary font-semibold hover:underline"
                            data-testid={`wiz-spin-sound-preview-${opt.id}`}
                          >
                            ▶ Preview
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              )}
              <div>
                <Label>Custom fields</Label>
                <div className="mt-2 space-y-2">
                  {form.custom_form_fields.map((f, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        className="col-span-7"
                        placeholder="Field label e.g. Birthday"
                        value={f.label}
                        onChange={(e) =>
                          update("custom_form_fields", form.custom_form_fields.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))
                        }
                      />
                      <Select value={f.type} onValueChange={(v) =>
                        update("custom_form_fields", form.custom_form_fields.map((x, idx) => idx === i ? { ...x, type: v } : x))
                      }>
                        <SelectTrigger className="col-span-4"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="tel">Phone</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="col-span-1"
                        onClick={() => update("custom_form_fields", form.custom_form_fields.filter((_, idx) => idx !== i))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() =>
                    update("custom_form_fields", [...form.custom_form_fields, { label: "", type: "text", options: [], required: false }])
                  }
                  data-testid="wiz-add-custom-field"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add field
                </Button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-semibold">Coupon settings</h2>
              <div className="space-y-1.5">
                <Label>Coupon code prefix</Label>
                <Input
                  value={form.coupon_prefix}
                  onChange={(e) => update("coupon_prefix", e.target.value.toUpperCase().slice(0, 12))}
                  placeholder="CTD-XMAS"
                  data-testid="wiz-coupon-prefix"
                />
                <p className="text-xs text-muted-foreground">Example: <code>{form.coupon_prefix}-XXXXXX</code></p>
              </div>
              <div className="space-y-1.5">
                <Label>Validity (days)</Label>
                <Input
                  type="number"
                  min={1}
                  max={365}
                  value={form.coupon_validity_days}
                  onChange={(e) => update("coupon_validity_days", e.target.value)}
                  data-testid="wiz-coupon-validity"
                />
              </div>

              {/* Terms & Conditions — read-only preview by default,
                  toggles into an editable textarea on click. Empty
                  `terms_and_conditions` means "use auto-fill"; we
                  render the same boilerplate the backend will
                  generate on save so the vendor previews the actual
                  outcome. Editing pre-fills the textarea with either
                  the current saved text OR the auto-fill preview so
                  the vendor never starts from a blank page. */}
              <TermsAndConditionsField
                value={form.terms_and_conditions}
                validityDays={form.coupon_validity_days}
                gameType={form.game_type}
                onChange={(next) => update("terms_and_conditions", next)}
              />
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <h2 className="font-heading text-xl font-semibold">Review & launch</h2>
              <div className="space-y-2 text-sm">
                <Row label="Name">{form.name}</Row>
                <Row label="Max participants">
                  <span className="tabular-nums">{maxParticipants}</span>
                </Row>
                <Row label="End date">
                  {form.expires_at ? new Date(form.expires_at).toLocaleDateString() : "No end date"}
                </Row>
                <Row label="Slices">
                  {form.slices.length} · <span className="tabular-nums text-muted-foreground">{totalQty} prizes total</span>
                </Row>
                <Row label={gameCopy.limitLabel}>{form.spin_limit.replace("_", " ")}</Row>
                <Row label="Coupon prefix">{form.coupon_prefix}</Row>
                <Row label="Validity">{form.coupon_validity_days} days</Row>
              </div>

              {launched ? (
                <Card className="p-5 border-emerald-500/20 bg-emerald-500/5">
                  <div className="flex items-start gap-4">
                    {launched.qr_code_url && (
                      <img src={assetUrl(launched.qr_code_url)} alt="QR" className="h-28 w-28 rounded-lg bg-white p-1" data-testid="launched-qr-image" />
                    )}
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-semibold">Live!</h3>
                      <p className="text-sm text-muted-foreground">Share this link or QR code with your customers.</p>
                      <div className="mt-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-mono break-all" data-testid="launched-url">
                        {launched.campaign_url}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <a href={launched.campaign_url} target="_blank" rel="noreferrer">
                          <Button variant="outline" size="sm"><ExternalLink className="h-4 w-4 mr-1" /> Open</Button>
                        </a>
                        {launched.qr_code_url && (
                          <a href={assetUrl(launched.qr_code_url)} download="campaign-qr.png">
                            <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1" /> Download QR</Button>
                          </a>
                        )}
                        <Button size="sm" onClick={() => navigate("/vendor/campaigns")}>Done</Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ) : (
                <>
                  {!percentIsExact && form.game_type !== "static_offer" && (
                    <p className="text-sm text-destructive flex items-center gap-2" data-testid="wiz-qty-block-text">
                      Slice percentages must sum to exactly 100%. Currently {Math.round(totalPct * 100) / 100}%.
                    </p>
                  )}
                  <Button
                    size="lg"
                    className="w-full h-12"
                    onClick={submit}
                    disabled={busy || !canSubmit}
                    data-testid="wiz-launch-button"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save changes" : "Launch campaign")}
                  </Button>
                </>
              )}
            </div>
          )}

          {!launched && (
            <div className="mt-6 flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
                data-testid="campaign-wizard-back-button"
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              {step < 5 ? (
                <Button
                  onClick={() => setStep(Math.min(5, step + 1))}
                  disabled={!canNext()}
                  data-testid="campaign-wizard-next-button"
                >
                  Next <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={busy || !canSubmit}
                  data-testid="campaign-wizard-save-button"
                >
                  {busy ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editing ? (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" /> Save changes
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" /> Launch campaign
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Live preview */}
        <div className="lg:col-span-5" data-testid="campaign-wizard-live-preview">
          {/*
            LIVE PREVIEW — mirrors PlayPage's exact composition so the
            vendor sees precisely what customers will see on /play. Uses:
              • composed theme (Category × Intensity × Manual Variant)
              • SVG RewardWheel (same component as PlayPage)
              • layered backdrop + texture + ambient spotlight
            Legacy `form.theme` is no longer read here — kept only for
            backward-compat with `themeConfettiColors` on the win popup.
          */}
          <div
            className="relative overflow-hidden rounded-2xl border border-border shadow-[var(--shadow-card)]"
            style={{
              minHeight: 620,
              background: previewTheme.backgroundBase,
              color: "#F5F5F5",
            }}
            data-testid="wiz-preview-surface"
          >
            {/* Texture overlay */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage: previewTheme.textureImage,
                backgroundRepeat: "repeat",
                opacity: previewTheme.textureOpacity,
                mixBlendMode: "screen",
              }}
            />
            {/* Ambient spotlight */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{
                background: `radial-gradient(55% 45% at 50% 38%, ${previewTheme.ambientColor} 0%, rgba(0,0,0,0) 65%)`,
                opacity: previewTheme.ambientOpacity,
              }}
            />

            <div className="relative z-10 px-4 pt-8 pb-10">
              <div className="text-center mb-5">
                {form.logo_url ? (
                  <div className="mx-auto mb-3 h-14 w-14 rounded-2xl overflow-hidden bg-white ring-2 ring-[#F5A800]/70 shadow-[4px_4px_0_rgba(245,168,0,0.35)] grid place-items-center">
                    <img
                      src={assetUrl(form.logo_url)}
                      alt="logo"
                      className="w-full h-full object-contain p-1.5"
                    />
                  </div>
                ) : (
                  <div
                    className="mx-auto mb-3 h-14 w-14 rounded-2xl bg-black grid place-items-center ring-2 ring-[#F5A800]/70 shadow-[4px_4px_0_rgba(245,168,0,0.35)]"
                    aria-hidden="true"
                  >
                    <span
                      className="text-[#F5A800]"
                      style={{ fontFamily: '"Anton", sans-serif', fontSize: 22 }}
                    >
                      {(form.name || "C").trim().charAt(0).toUpperCase() || "C"}
                    </span>
                  </div>
                )}
                <h1
                  className="leading-[0.95] tracking-tight text-3xl uppercase text-white"
                  style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
                  data-testid="wiz-preview-title"
                >
                  {form.game_type === "static_offer" ? (
                    // "Claim Your Offer" — no & separator to keep the
                    // headline short since /offers/{id} shows one prize
                    // as a card, not a mechanic.
                    <>Claim Your <span className="text-[#F5A800]">Offer</span></>
                  ) : form.game_type === "scratch_card" ? (
                    <>Scratch <span className="text-[#F5A800]">&amp;</span> Win</>
                  ) : (
                    <>Spin <span className="text-[#F5A800]">&amp;</span> Win</>
                  )}
                </h1>
                <p className="mt-2 text-sm font-semibold text-white/90">
                  {form.name || "Campaign name"}
                </p>
                <p className="mt-1 text-[11px] text-white/60">
                  {previewTheme.label} · {form.theme_intensity || "branded"}
                  {form.manual_theme_variant ? ` · ${form.manual_theme_variant}` : ""}
                </p>
              </div>

              <div className="mt-2 grid place-items-center">
                {form.game_type === "static_offer" ? (
                  // Static-offer preview — no wheel, no scratch. Show
                  // the single prize as a large centered card so the
                  // vendor can visualise what the /offers/{id} page
                  // will look like.
                  <div
                    className="w-full max-w-xs rounded-2xl border-2 border-black bg-white p-5 text-center"
                    style={{ boxShadow: "6px 6px 0 rgba(0,0,0,0.6)" }}
                    data-testid="wiz-preview-static-offer"
                  >
                    <p className="text-xs uppercase tracking-widest text-black/60 font-bold mb-2">
                      {gameCopy.winTitle}
                    </p>
                    <p
                      className="text-2xl font-black text-black leading-tight"
                      style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
                    >
                      {(form.slices[0]?.gift_name || form.slices[0]?.label || "Your Offer").toString()}
                    </p>
                    <p className="text-xs text-black/60 mt-2">
                      {maxParticipants} available · Valid for {form.coupon_validity_days || 30} days
                    </p>
                  </div>
                ) : form.game_type === "scratch_card" ? (
                  <ScratchCard
                    slices={form.slices}
                    logoUrl={form.logo_url}
                    primaryColor={previewTheme.sliceA || form.primary_color || "#F5A800"}
                    secondaryColor={previewTheme.sliceB}
                    onSliceText={previewTheme.onSlice}
                    spinningTo={null}
                    onSpinEnd={() => {}}
                    onScratchThreshold={() => {}}
                    scratchSound={form.scratch_sound || "classic"}
                    campaignName={form.name || "Scratch & Win"}
                    testId="wiz-preview-scratch"
                  />
                ) : (
                  <RewardWheel
                    slices={form.slices}
                    logoUrl={form.logo_url}
                    primaryColor={previewTheme.sliceA || form.primary_color || "#F5A800"}
                    secondaryColor={previewTheme.sliceB}
                    onSliceText={previewTheme.onSlice}
                    spinningTo={null}
                    onSpinEnd={() => {}}
                    testId="wiz-preview-canvas"
                  />
                )}
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  className="h-12 px-8 rounded-full text-sm font-bold uppercase tracking-wider transition-transform hover:-translate-y-0.5"
                  style={{
                    backgroundColor: "#F5A800",
                    color: "#0A0A0A",
                    boxShadow: "4px 4px 0 #0A0A0A",
                    fontFamily: '"Anton", "Impact", sans-serif',
                    letterSpacing: "0.08em",
                  }}
                  data-testid="wiz-preview-spin-btn"
                >
                  {gameCopy.ctaLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{children}</span>
  </div>
);

const ThemeBadge = ({ theme }) => {
  const meta = getThemeMeta(theme);
  // Premium themes have a preview image in object storage
  if (meta.preview) {
    return (
      <div className="relative aspect-[5/3] w-full overflow-hidden bg-muted">
        <img
          src={assetUrl(meta.preview)}
          alt={`${meta.label} theme preview`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
          data-testid={`theme-preview-${theme}`}
        />
      </div>
    );
  }
  // Legacy fallback gradient swatches
  const grad = {
    citydooh: "linear-gradient(135deg,#000000,#F5A800 70%,#FFFFFF)",
    christmas: "linear-gradient(135deg,#0F2A20,#E11D48 60%,#F5D061)",
    diwali: "linear-gradient(135deg,#140A1F,#F59E0B 60%,#F97316)",
    newyear: "linear-gradient(135deg,#070A12,#F5D061 60%,#22D3EE)",
    valentine: "linear-gradient(135deg,#FFF1F2,#E11D48 70%,#FB7185)",
    birthday: "linear-gradient(135deg,#F0F9FF,#0EA5E9 60%,#F97316)",
    sale: "linear-gradient(135deg,#FFF7ED,#DC2626 70%,#F59E0B)",
    custom: "linear-gradient(135deg,#0a0a0a,#F5A800,#0a0a0a)",
  };
  return (
    <div
      className="aspect-[5/3] w-full"
      style={{ background: grad[theme] || grad.custom }}
    />
  );
};

// ---------------------------------------------------------------
// BillingBanner — surface the trial / override / paid state to the
// vendor before they hit "Create campaign" so there are no surprises.
// ---------------------------------------------------------------
const BillingBanner = ({ billing }) => {
  if (!billing) return null;
  if (billing.override_active) {
    const note = (billing.override_note || "").trim();
    return (
      <div
        className="rounded-xl border-2 border-black bg-[var(--graffiti-yellow)] px-4 py-3 flex items-start gap-3 shadow-[5px_5px_0_#0a0a0a]"
        data-testid="wizard-billing-override"
      >
        <div className="text-sm text-black">
          <p className="font-bold uppercase tracking-wide">Complimentary access</p>
          <p className="opacity-85">
            {billing.override_label} — this campaign is on the house. No payment required.
            {note ? ` Note: ${note}` : ""}
          </p>
        </div>
      </div>
    );
  }
  if (billing.trial_available) {
    return (
      <div
        className="rounded-xl border-2 border-black bg-white px-4 py-3 flex items-start gap-3 shadow-[5px_5px_0_#0a0a0a]"
        data-testid="wizard-billing-trial"
      >
        <div className="text-sm text-black">
          <p className="font-bold uppercase tracking-wide">15-day free trial</p>
          <p className="opacity-80">
            Your first campaign is free for {billing.trial_days} days or {billing.trial_lead_cap} leads — whichever comes first.
            After that you pay ₹{billing.campaign_fee} per campaign.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="wizard-billing-paid"
      className="rounded-xl border-2 border-black bg-white px-4 py-3 flex items-start gap-3 shadow-[5px_5px_0_#0a0a0a]"
    >
      <div className="text-sm text-black">
        <p className="font-bold uppercase tracking-wide">₹{billing.campaign_fee} per campaign + 18% GST</p>
        <p className="opacity-80">
          Your free trial has been used. After you click <b>Create campaign</b> we&#39;ll take you to a checkout page with the full breakdown (base + GST + total) — then Razorpay.
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------
// TermsAndConditionsField — read-only preview + Edit toggle for the
// campaign-level T&C.
//
// Behaviour:
//  - When `value` is empty AND user hasn't clicked Edit → render the
//    client-computed auto-fill (mirrors backend `_default_terms`) as
//    read-only. The empty string is preserved on save so the backend
//    stays authoritative on the boilerplate.
//  - When `value` is non-empty → render the saved text as read-only.
//  - Click Edit → textarea pre-filled with `value` OR the auto-fill
//    (never blank). Save writes the textarea contents back to the
//    form. Reset clears back to auto-fill.
// ---------------------------------------------------------------
const TermsAndConditionsField = ({ value, validityDays, gameType, onChange }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const autoFill = React.useMemo(
    () => buildDefaultTerms(validityDays, gameType),
    [validityDays, gameType],
  );
  const displayed = (value && value.trim()) ? value : autoFill;
  const isAutoFilled = !(value && value.trim());

  const startEditing = () => {
    setDraft(displayed);
    setEditing(true);
  };
  const cancelEditing = () => {
    setDraft("");
    setEditing(false);
  };
  const saveEditing = () => {
    onChange(draft);
    setEditing(false);
  };
  const resetToAutoFill = () => {
    onChange("");
    setDraft("");
    setEditing(false);
  };

  return (
    <div className="space-y-1.5" data-testid="wiz-tnc-field">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label>Terms &amp; Conditions</Label>
        {!editing ? (
          <div className="flex items-center gap-2">
            {isAutoFilled && (
              <span
                className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-border text-muted-foreground"
                data-testid="wiz-tnc-autofill-badge"
              >
                Auto-filled
              </span>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={startEditing}
              data-testid="wiz-tnc-edit-button"
            >
              Edit
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={resetToAutoFill}
              data-testid="wiz-tnc-reset-button"
            >
              Reset to default
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={cancelEditing}
              data-testid="wiz-tnc-cancel-button"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={saveEditing}
              data-testid="wiz-tnc-save-button"
            >
              Save
            </Button>
          </div>
        )}
      </div>
      {!editing ? (
        <div
          className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed max-h-56 overflow-auto"
          data-testid="wiz-tnc-readonly"
        >
          {displayed}
        </div>
      ) : (
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={10}
          maxLength={4000}
          className="font-mono text-xs leading-relaxed"
          data-testid="wiz-tnc-textarea"
          placeholder="Write your campaign terms and conditions..."
        />
      )}
      <p className="text-[11px] text-muted-foreground">
        Shown on the play page and in coupon emails. Leave as auto-fill
        for our standard boilerplate, or Edit to customise.
      </p>
    </div>
  );
};


// ---------------------------------------------------------------
// CouponSourceField — per-slice coupon-source selector.
//
// Three modes:
//   * "auto"   — Default. Server generates CTD codes at spin time.
//                No extra fields to configure.
//   * "single" — All winners receive the SAME external code
//                (e.g. a partner's discount code). Delivered only —
//                the /verify + Leads redemption UIs will render
//                "Delivered" instead of a Redeem button, matching
//                the redemption_trackable=False stored on each
//                coupon record.
//   * "pool"   — Vendor uploads a bulk list of unique codes. Each
//                winner atomically claims a distinct code. Fully
//                trackable, same UX as auto-mode.
//
// Pool uploads now happen INSIDE the wizard on the ORIGINAL create
// flow — codes are parsed client-side, validated for exact-count
// match against `expectedCount`, and buffered on the slice object as
// `_pending_pool_codes`. On final submit, the parent bundles them
// into `pool_codes_by_slice` on the CampaignCreate payload; the
// server re-parses / re-validates and does the actual insert.
//
// For existing (already-persisted) campaigns we KEEP the append-only
// upload path via `api.campaignPoolUpload` so vendors can top up
// codes on a live pool without waiting for a redeploy.
// ---------------------------------------------------------------
const CouponSourceField = ({ slice, index, campaignId, expectedCount, onChange }) => {
  const [refreshing, setRefreshing] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [parseInvalid, setParseInvalid] = React.useState([]);
  const mode = (slice.coupon_mode || "auto").toLowerCase();
  const isPersisted = !!campaignId && !!slice.id;
  // Client-parsed codes buffered on the slice while the campaign
  // hasn't been created yet. Never round-tripped for auto/single.
  const pendingCodes = Array.isArray(slice._pending_pool_codes)
    ? slice._pending_pool_codes
    : [];

  const modes = React.useMemo(() => ([
    { value: "auto",   label: "Auto-generate (default)",
      hint: "We generate a unique CTD-prefixed coupon for every winner." },
    { value: "single", label: "Single shared code",
      hint: "Used exactly as entered — for external/partner discount codes. Delivery only, not tracked as redeemable." },
    { value: "pool",   label: "Upload code list",
      hint: "Bulk-upload a list of unique codes; each winner claims one." },
  ]), []);

  const refreshPool = React.useCallback(async () => {
    if (!isPersisted) return;
    setRefreshing(true);
    try {
      const r = await api.campaignPoolSummary(campaignId, slice.id);
      onChange("pool_codes_total", r.data.pool_codes_total);
      onChange("pool_codes_remaining", r.data.pool_codes_remaining);
    } catch (err) {
      // 404 == slice not yet persisted on backend after a recent
      // reorder; safely ignore.
    } finally {
      setRefreshing(false);
    }
  }, [isPersisted, campaignId, slice.id, onChange]);

  React.useEffect(() => {
    if (mode === "pool" && isPersisted) refreshPool();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, isPersisted]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();

      // Path A: campaign not yet persisted (fresh create in the
      // wizard). Parse client-side and buffer the codes on the slice.
      // No server round-trip — server re-parses on submit anyway.
      if (!isPersisted) {
        const { codes, invalid } = parsePoolCodes(text);
        onChange("_pending_pool_codes", codes);
        // Mirror the counters so the rest of the UI (Review, footer)
        // can display the count without inspecting internal state.
        onChange("pool_codes_total", codes.length);
        onChange("pool_codes_remaining", codes.length);
        setParseInvalid(invalid);
        if (codes.length === 0) {
          toast.error("Couldn't read any codes from that file.");
        } else if (expectedCount && codes.length !== expectedCount) {
          // Client-side mismatch is surfaced via the red banner
          // below — don't stack a scary red toast on top.
          toast(`Parsed ${codes.length} code${codes.length === 1 ? "" : "s"} — file needs adjustment`);
        } else {
          toast.success(`Parsed ${codes.length} code${codes.length === 1 ? "" : "s"} — ready to launch`);
        }
        return;
      }

      // Path B: campaign already exists (editing / topping up an
      // active pool). Keep the append-only server upload flow.
      const r = await api.campaignPoolUpload(campaignId, slice.id, text);
      const d = r.data || {};
      onChange("pool_codes_total", d.pool_codes_total);
      onChange("pool_codes_remaining", d.pool_codes_remaining);
      const parts = [`${d.added || 0} added`];
      if ((d.skipped_duplicate || []).length) parts.push(`${d.skipped_duplicate.length} duplicate`);
      if ((d.skipped_invalid || []).length) parts.push(`${d.skipped_invalid.length} invalid`);
      toast.success(parts.join(", "));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Clear parsed codes when the vendor switches away from pool mode
  // so they don't accidentally ship on a create request.
  React.useEffect(() => {
    if (mode !== "pool" && (slice._pending_pool_codes || []).length) {
      onChange("_pending_pool_codes", []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const activeHint = modes.find((m) => m.value === mode)?.hint;

  // Exact-count mismatch — the moment the vendor picks a file OR
  // changes the quantity, the banner below re-computes and Next is
  // blocked (parent reads `_pending_pool_codes.length` vs the same
  // `expectedCount` in `canNext`/`canSubmit`).
  const poolCount = pendingCodes.length;
  const hasUploaded = poolCount > 0;
  const countMismatch = hasUploaded && !!expectedCount && poolCount !== expectedCount;

  return (
    <div
      className="mt-2 rounded-lg border border-dashed border-border/70 bg-muted/20 p-3 space-y-2"
      data-testid={`wiz-slice-coupon-source-${index}`}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
          Coupon source
        </div>
        <div className="text-[10px] text-muted-foreground">
          {mode === "single" ? "External · not tracked" : mode === "pool" ? "Per-winner from pool · tracked" : "Auto-generated · tracked"}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {modes.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => onChange("coupon_mode", m.value)}
            className={
              "text-xs px-3 py-1.5 rounded-full border transition " +
              (mode === m.value
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border hover:bg-muted")
            }
            data-testid={`wiz-slice-${index}-mode-${m.value}`}
          >
            {m.label}
          </button>
        ))}
      </div>
      <p className="text-[10px] text-muted-foreground leading-snug">{activeHint}</p>

      {mode === "single" && (
        <div className="space-y-1">
          <Input
            value={slice.single_code || ""}
            onChange={(e) => onChange("single_code", e.target.value.slice(0, 64))}
            placeholder="e.g. PARTNER50 (used exactly as typed)"
            className="text-xs font-mono"
            maxLength={64}
            data-testid={`wiz-slice-${index}-single-code`}
          />
        </div>
      )}

      {mode === "pool" && (
        <div className="space-y-2" data-testid={`wiz-slice-${index}-pool-panel`}>
          {/* Template download + upload sit on one row. Always
              visible — a fresh create AND an existing campaign both
              benefit from the canonical template. */}
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`${API}/coupon-pool/template.csv`}
              download="citydooh-coupon-codes-template.csv"
              className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted"
              data-testid={`wiz-slice-${index}-pool-template`}
            >
              <Download className="h-3.5 w-3.5" /> Download template
            </a>
            <label className={
              "inline-flex items-center gap-2 cursor-pointer text-xs px-3 py-1.5 rounded-lg border transition " +
              (uploading ? "opacity-60 pointer-events-none " : "") +
              "border-border hover:bg-muted"
            }>
              <Upload className="h-3.5 w-3.5" />
              {uploading ? "Reading…" : hasUploaded ? "Replace file" : "Upload CSV / TXT"}
              <input
                type="file"
                accept=".csv,.txt,text/plain,text/csv"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) handleUpload(f);
                }}
                disabled={uploading}
                data-testid={`wiz-slice-${index}-pool-upload`}
              />
            </label>
            {isPersisted && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={refreshPool}
                disabled={refreshing}
                className="h-7 px-2 text-[11px]"
              >
                Refresh
              </Button>
            )}
          </div>

          {/* Counters — sourced from client state for fresh creates,
              from server state for existing campaigns. */}
          {(isPersisted || hasUploaded) && (
            <div
              className="text-[11px] text-muted-foreground font-mono tabular-nums"
              data-testid={`wiz-slice-${index}-pool-counters`}
            >
              {isPersisted
                ? `${Number(slice.pool_codes_remaining || 0)} / ${Number(slice.pool_codes_total || 0)} codes remaining`
                : `${poolCount} code${poolCount === 1 ? "" : "s"} parsed`}
            </div>
          )}

          {/* HARD-TO-MISS red banner when the parsed count doesn't
              equal the declared quantity. This is spec item #4 —
              exact-count validation; wizard progression is blocked
              via `canNext` / `canSubmit` in the parent. */}
          {countMismatch && (
            <div
              className="rounded-lg border-2 border-destructive/60 bg-destructive/10 text-destructive px-3 py-2.5 text-xs font-medium"
              role="alert"
              data-testid={`wiz-slice-${index}-pool-mismatch`}
            >
              <div className="flex items-start gap-2">
                <span aria-hidden="true">⚠</span>
                <div className="space-y-1">
                  <p className="font-semibold">
                    You set {expectedCount} code{expectedCount === 1 ? "" : "s"} but uploaded {poolCount}.
                  </p>
                  <p className="opacity-90">
                    Fix your file or update the quantity above before continuing. The uploaded file must contain <b>exactly</b> {expectedCount} valid code{expectedCount === 1 ? "" : "s"}.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Any codes we chose to drop during parsing — surface so
              vendors know their file wasn't 100% clean even if the
              count happens to match. */}
          {(parseInvalid || []).length > 0 && (
            <div
              className="text-[10px] text-amber-700 dark:text-amber-400 bg-amber-100/40 dark:bg-amber-900/20 rounded px-2 py-1.5"
              data-testid={`wiz-slice-${index}-pool-invalid`}
            >
              Skipped {parseInvalid.length} row{parseInvalid.length === 1 ? "" : "s"} while parsing (header rows or oversized values).
            </div>
          )}

          {!hasUploaded && !isPersisted && (
            <p className="text-[10px] text-muted-foreground leading-snug">
              Tip: download the template above, replace <code>SAMPLE001…</code> with your real codes, then upload. Your quantity ({expectedCount || "—"}) sets the required row count.
            </p>
          )}
          {isPersisted && (
            <p className="text-[10px] text-muted-foreground leading-snug">
              Codes are added, never overwritten — safe to upload more anytime to top up the pool.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

